import React, { useState } from 'react';
import { Card } from '../ui/Card';

export function InsightsTab() {
  const [filter, setFilter] = useState<'all' | 'information' | 'encouragement'>('all');

  const entries = [
    {
      id: 1,
      category: 'encouragement',
      title: 'I Am Here to Assist Lost Souls',
      teaser: 'The most powerful thing you can offer someone who is trapped is proof that someone who was also trapped found a way through — and came back to show them.',
      body: 'This is the mission statement...',
      isLocked: false,
    },
    {
      id: 2,
      category: 'information',
      title: 'Hyperinsulinemia — The Overflow Model',
      teaser: 'Insulin resistance implies your body is broken. The overflow model says it is simply full.',
      body: 'The medical establishment frames obesity...',
      isLocked: false,
    },
    {
      id: 3,
      category: 'information',
      title: 'Dysgeusia — Your Taste Will Betray You',
      teaser: 'Extended fasting causes a predictable taste disorder...',
      isLocked: true,
    },
  ];

  const filteredEntries = entries.filter((entry) => filter === 'all' || entry.category === filter);

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-2">
        <p className="text-xs text-on-surface-variant">
          26 entries from 222 days of survival, relapse, and return.
        </p>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {(['all', 'information', 'encouragement'] as const).map((f) => (
            <button
              key={f}
              className={`
                rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap
                ${filter === f
                  ? 'bg-primary text-primary-contrast'
                  : 'bg-surface text-on-surface hover:bg-on-surface/10'}
              `}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <SectionLabel>Foundation</SectionLabel>
        {filteredEntries.map((entry) => (
          <InsightCard key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
      {children}
    </h4>
  );
}

function InsightCard({ entry }: { entry: typeof entries[0] }) {
  const [expanded, setExpanded] = useState(false);

  if (entry.isLocked) {
    return (
      <Card className="opacity-60">
        <div className="flex items-start justify-between">
          <div>
            <span className="inline-block rounded bg-on-surface/10 px-2 py-0.5 text-[10px] text-on-surface-variant">
              {entry.category}
            </span>
            <h3 className="mt-2 font-semibold text-on-surface">{entry.title}</h3>
          </div>
          <span className="text-2xl">🔒</span>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <span className="inline-block rounded bg-on-surface/10 px-2 py-0.5 text-[10px] text-on-surface-variant">
            {entry.category}
          </span>
          <h3 className="mt-2 font-semibold text-on-surface">{entry.title}</h3>
          <p className="mt-1 text-sm text-on-surface-variant">{entry.teaser}</p>
          <button
            className="mt-2 text-sm font-medium text-primary hover:underline"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? '▴ Close' : '▾ Read'}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-outline">
          <p className="text-sm text-on-surface">{entry.body}</p>
        </div>
      )}
    </Card>
  );
}
